/** Composants d'UI réutilisables (KPI, carte de graphique, barre de progression,
 * sélecteur segmenté, bannières). */
import { type ReactNode, useId } from 'react';
import { Link } from 'react-router-dom';
import {
  type Granularity,
  PERIOD_LABELS,
  type Period,
  TRAINING_PHASE_LABELS,
  type TrainingPhase,
} from '@/domain';

/** Badge de la phase d'entraînement active. La teinte par phase (via data-phase)
 * est définie dans theme.css ; rappelle « dans quel mode » on lit les dashboards. */
export function PhaseBadge({ phase }: { phase: TrainingPhase }) {
  return (
    <span className="badge phase" data-phase={phase}>
      {TRAINING_PHASE_LABELS[phase]}
    </span>
  );
}

interface KpiProps {
  label: string;
  value: ReactNode;
  unit?: string;
  /** Variation optionnelle : signe → couleur (hausse rouge, baisse verte).
   * `neutral` force l'absence de jugement (ni bien ni mal — ex. récupération). */
  delta?: { value: number; unit?: string; goodWhenNegative?: boolean; neutral?: boolean };
  hint?: string;
  /** Si présent, la tuile devient un lien vers cette route. */
  to?: string;
}

/** Tuile KPI : un chiffre-titre + variation colorée + libellé. */
export function Kpi({ label, value, unit, delta, hint, to }: KpiProps) {
  let deltaClass = 'neutral';
  let deltaText = '';
  if (delta && Number.isFinite(delta.value) && delta.value !== 0) {
    if (!delta.neutral) {
      const improving = delta.goodWhenNegative ? delta.value < 0 : delta.value > 0;
      deltaClass = improving ? 'down' : 'up';
    }
    const sign = delta.value > 0 ? '+' : '';
    deltaText = `${sign}${delta.value}${delta.unit ?? ''}`;
  }
  const card = (
    <div className="card kpi">
      <span className="label">{label}</span>
      <span className="value">
        {value}
        {unit && <small>{unit}</small>}
      </span>
      {delta && deltaText && <span className={`delta ${deltaClass}`}>{deltaText}</span>}
      {hint && (
        <span className="muted" style={{ fontSize: '0.76rem' }}>
          {hint}
        </span>
      )}
    </div>
  );
  return to ? (
    // Pas d'aria-label : le lien tire son nom accessible du contenu (libellé + valeur).
    <Link to={to} className="kpi-link">
      {card}
    </Link>
  ) : (
    card
  );
}

/** Carte contenant un graphique, avec titre et sous-titre optionnels. */
export function ChartCard({
  title,
  subtitle,
  children,
  actions,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ marginBottom: subtitle ? 2 : 0 }}>{title}</h3>
          {subtitle && (
            <span className="muted" style={{ fontSize: '0.8rem' }}>
              {subtitle}
            </span>
          )}
        </div>
        {actions}
      </div>
      <div style={{ marginTop: '0.75rem' }}>{children}</div>
    </div>
  );
}

/** Barre de progression avec pourcentage. */
export function ProgressBar({ pct, color }: { pct: number; color?: string }) {
  return (
    <div
      className="progress"
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <span style={{ width: `${Math.min(100, Math.max(0, pct))}%`, background: color }} />
    </div>
  );
}

/** Sélecteur segmenté accessible (rôle `radiogroup` + navigation clavier fléchée). */
export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly { key: T; label: string }[];
  onChange: (value: T) => void;
}) {
  const idx = options.findIndex((o) => o.key === value);
  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const delta = e.key === 'ArrowRight' ? 1 : -1;
    const next = options[(idx + delta + options.length) % options.length];
    if (next) onChange(next.key);
  }
  return (
    <div className="segmented" role="radiogroup" aria-label={label} onKeyDown={onKeyDown}>
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          role="radio"
          aria-checked={value === o.key}
          tabIndex={value === o.key ? 0 : -1}
          className={value === o.key ? 'active' : ''}
          onClick={() => onChange(o.key)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const GRANULARITIES: { key: Granularity; label: string }[] = [
  { key: 'day', label: 'Jour' },
  { key: 'week', label: 'Semaine' },
  { key: 'month', label: 'Mois' },
];

/** Sélecteur segmenté jour / semaine / mois. */
export function GranularityPicker({
  value,
  onChange,
}: {
  value: Granularity;
  onChange: (g: Granularity) => void;
}) {
  return (
    <Segmented label="Granularité" value={value} options={GRANULARITIES} onChange={onChange} />
  );
}

const PERIODS: { key: Period; label: string }[] = (['7d', '30d', '90d', 'all'] as const).map(
  (p) => ({
    key: p,
    label: PERIOD_LABELS[p],
  }),
);

/** Sélecteur segmenté de période (7 / 30 / 90 j / tout). */
export function PeriodPicker({
  value,
  onChange,
}: {
  value: Period;
  onChange: (p: Period) => void;
}) {
  return <Segmented label="Période" value={value} options={PERIODS} onChange={onChange} />;
}

/** Bannière d'erreur (formulaire ou chargement de données), avec réessai optionnel. */
export function ErrorBanner({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="banner error" role="alert">
      <span>⚠️ {message}</span>
      {onRetry && (
        <button type="button" className="ghost" onClick={onRetry}>
          Réessayer
        </button>
      )}
    </div>
  );
}

/** État vide générique. */
export function EmptyState({ children }: { children: ReactNode }) {
  return (
    <div
      className="card"
      style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}
    >
      {children}
    </div>
  );
}

/**
 * Curseur avec zone recommandée (santé publique) visualisée sur la piste + note
 * sourcée. L'utilisateur reste libre de régler hors zone : on signale, on ne bloque pas.
 */
export function RangeWithGuideline({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  reco,
  note,
  disabled,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step?: number;
  /** Bornes recommandées (dans [min, max]) mises en évidence. */
  reco?: { min: number; max: number };
  /** Note sourcée affichée sous le curseur (liée via aria-describedby). */
  note?: string;
  disabled?: boolean;
}) {
  const id = useId();
  const noteId = `${id}-note`;
  const span = max - min || 1;
  const bandLeft = reco ? ((Math.max(min, reco.min) - min) / span) * 100 : 0;
  const bandWidth = reco ? ((Math.min(max, reco.max) - Math.max(min, reco.min)) / span) * 100 : 0;

  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <div className="range-wrap">
        {reco && (
          <span
            className="range-reco"
            aria-hidden="true"
            style={{ left: `${bandLeft}%`, width: `${bandWidth}%` }}
          />
        )}
        <input
          id={id}
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          disabled={disabled}
          aria-describedby={note ? noteId : undefined}
          onChange={(e) => onChange(Number(e.target.value))}
        />
      </div>
      {note && (
        <span id={noteId} className="muted" style={{ fontSize: '0.78rem' }}>
          {note}
        </span>
      )}
    </div>
  );
}
